import { useId, useMemo } from "react";
import { Activity, ChevronDown } from "lucide-react";
import { SERIES_COLORS } from "@/constants/chart-constants.js";
import { cn } from "@/lib/utils.js";
import { rankKeyTrends } from "@/components/root-cause-analysis/rca-key-trend-score.js";
import RcaMeasurementFilter from "@/components/root-cause-analysis/rca-measurement-filter.jsx";
import RcaMeasurementLabelHighlight from "@/components/root-cause-analysis/rca-measurement-label-highlight.jsx";
import { sortKeyedTrendEntries } from "@/components/root-cause-analysis/rca-measurement-sort.js";
import RcaMeasurementSortToolbar from "@/components/root-cause-analysis/rca-measurement-sort-toolbar.jsx";
import {
  getMeasurementLabel,
  measurementLabelMatchesFilters,
} from "@/components/root-cause-analysis/root-cause-analysis-helpers.js";
import { useControllableState } from "@/components/root-cause-analysis/use-controllable-state.js";
import {
  RCA_TREND_CARD_CLASS,
  RCA_TREND_LIST_CLASS,
  RcaTrendCardRow,
} from "@/components/root-cause-analysis/rca-measurement-trend-shared.jsx";

/**
 * Sensors whose weighted key-trend score meets the configurable threshold.
 *
 * @param {{
 *   rows: unknown[],
 *   overallFilterText?: string,
 *   filterText?: string,
 *   onFilterTextChange?: (value: string) => void,
 *   zScoreThreshold: number,
 *   keyTrendScoreThreshold: number,
 *   useAlarmLimits?: boolean,
 *   sortMode?: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode,
 *   onSortModeChange?: (mode: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode) => void,
 *   sortDirection?: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection,
 *   onSortDirectionChange?: (direction: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection) => void,
 *   expanded?: boolean,
 *   onExpandedChange?: (expanded: boolean) => void,
 * }} props
 * @returns {JSX.Element}
 */
export default function KeySensorSignalsBeforeShutdown({
  rows,
  overallFilterText = "",
  filterText: filterTextProp,
  onFilterTextChange,
  zScoreThreshold,
  keyTrendScoreThreshold,
  useAlarmLimits = true,
  sortMode: sortModeProp,
  onSortModeChange,
  sortDirection: sortDirectionProp,
  onSortDirectionChange,
  expanded: expandedProp,
  onExpandedChange,
}) {
  const [expanded, setExpanded] = useControllableState(
    expandedProp,
    onExpandedChange,
    false
  );
  const [filterText, setFilterText] = useControllableState(
    filterTextProp,
    onFilterTextChange,
    ""
  );
  const [sortMode, setSortMode] = useControllableState(
    sortModeProp,
    onSortModeChange,
    /** @type {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode} */ (
      "score"
    )
  );
  const [sortDirection, setSortDirection] = useControllableState(
    sortDirectionProp,
    onSortDirectionChange,
    /** @type {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection} */ (
      "desc"
    )
  );
  const headingId = useId();

  function toggleExpanded() {
    setExpanded(!expanded);
  }

  const scoreOptions = useMemo(
    () => ({
      useAlarmLimits,
      zScoreThreshold,
      keyTrendScoreThreshold,
    }),
    [useAlarmLimits, zScoreThreshold, keyTrendScoreThreshold]
  );

  const allKeyTrends = useMemo(
    () => rankKeyTrends(rows, scoreOptions),
    [rows, scoreOptions]
  );

  const keyTrends = useMemo(
    () => {
      const filtered = allKeyTrends.filter(({ row }) => {
        const label = getMeasurementLabel(row);
        return measurementLabelMatchesFilters(label, {
          sectionFilterText: filterText,
          overallFilterText,
        });
      });

      return sortKeyedTrendEntries(filtered, { sortMode, sortDirection });
    },
    [allKeyTrends, sortMode, sortDirection, filterText, overallFilterText]
  );
  const filteredCount = keyTrends.length;
  const totalCount = allKeyTrends.length;

  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <section className='rounded-lg border border-border bg-muted/60 px-2 py-2 sm:px-3'>
        <h3 className='text-sm font-semibold text-foreground'>
          Key Sensor Signals Before Shutdown (0)
        </h3>
        <p className='mt-2 text-xs text-muted-foreground'>
          No measurement data for this window yet.
        </p>
      </section>
    );
  }

  return (
    <section className='rounded-lg border border-border bg-muted/60 px-2 py-2 sm:px-3'>
      <div className='flex w-full items-start gap-2'>
        <button
          type='button'
          className='flex min-w-0 flex-1 items-start gap-2 rounded-md text-left transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={expanded ? `${headingId}-panel` : undefined}
          id={headingId}>
          <Activity
            className='mt-0.5 h-5 w-5 shrink-0 text-muted-foreground'
            aria-hidden
          />
          <div className='min-w-0 flex-1'>
            <h3 className='text-sm font-semibold text-foreground'>
              Key Sensor Signals Before Shutdown ({filteredCount} / {totalCount})
            </h3>
            <p className='mt-1 text-xs text-muted-foreground'>
              Trends with a weighted score &ge; {keyTrendScoreThreshold} (max
              100 pts:{" "}
              {useAlarmLimits
                ? "threshold breach, drift, step change, threshold approach"
                : "z-score, drift, step change"}
              ).
              {useAlarmLimits ? (
                <>
                  {" "}
                  Grey dashed line = average; yellow bands = Lo/Hi warning, red
                  bands = LoLo/HiHi critical.
                </>
              ) : (
                <> Grey dashed line = average.</>
              )}
            </p>
          </div>
        </button>
        <button
          type='button'
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={expanded ? `${headingId}-panel` : undefined}
          aria-label={
            expanded
              ? "Collapse key sensor signals"
              : "Expand key sensor signals"
          }>
          <ChevronDown
            className={cn(
              "h-6 w-6 transition-transform",
              expanded && "rotate-180"
            )}
            aria-hidden
          />
        </button>
      </div>

      {expanded ? (
        <div
          id={`${headingId}-panel`}
          className='mt-2 border-t border-border/80 pt-2'
          aria-labelledby={headingId}>
          <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
            <RcaMeasurementFilter
              filterText={filterText}
              onFilterTextChange={setFilterText}
            />
            <RcaMeasurementSortToolbar
              sortMode={sortMode}
              onSortModeChange={setSortMode}
              sortDirection={sortDirection}
              onSortDirectionChange={setSortDirection}
            />
          </div>
          {filteredCount === 0 ? (
            <p className='text-xs text-muted-foreground'>
              {filterText.trim()
                ? "No measurements match the current filter."
                : `No sensors with a key-trend score ≥ ${keyTrendScoreThreshold} in this window.`}
            </p>
          ) : (
              <ul className={RCA_TREND_LIST_CLASS}>
              {keyTrends.map(({ row, score }, index) => {
                const lineColor =
                  SERIES_COLORS[index % SERIES_COLORS.length] ?? "#0ea5e9";
                const label = getMeasurementLabel(row);
                const highlightedLabel = (
                  <RcaMeasurementLabelHighlight
                    label={label}
                    filterText={filterText.trim() || overallFilterText}
                  />
                );
                const uom = String(row.UoM ?? "").trim();

                return (
                  <li
                    key={row.AssetTagId ?? index}
                    className={RCA_TREND_CARD_CLASS}>
                    <RcaTrendCardRow
                      row={row}
                      lineColor={lineColor}
                      label={highlightedLabel}
                      uom={uom}
                      useAlarmLimits={useAlarmLimits}
                      score={score}
                    />
                  </li>
                );
              })}
              </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}
